<template>
  <div v-if="isDevMode" class="dev-tools">
    <div class="dev-tools-toggle" @click="togglePanel">
      <span v-if="!showPanel">🛠️</span>
      <span v-else>✖️</span>
    </div>
    
    <transition name="slide">
      <div v-if="showPanel" class="dev-tools-panel">
        <div class="dev-tools-header">
          <h3>🚀 开发工具</h3>
          <span class="dev-badge">DEV</span>
        </div>
        
        <div class="dev-tools-content">
          <!-- 认证状态 -->
          <div class="dev-section">
            <h4>认证状态</h4>
            <div class="dev-info">
              <div class="info-item">
                <span class="label">Token:</span>
                <span class="value" :class="{ 'has-token': hasToken }">
                  {{ hasToken ? '✓ 已设置' : '✗ 未设置' }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">用户ID:</span>
                <span class="value">{{ umtId || '未设置' }}</span>
              </div>
              <div class="info-item">
                <span class="label">环境:</span>
                <span class="value">{{ baseURL }}</span>
              </div>
            </div>
          </div>

          <!-- 快速操作 -->
          <div class="dev-section">
            <h4>快速操作</h4>
            <div class="dev-actions">
              <button @click="autoLogin" class="dev-btn primary">
                🔑 自动登录
              </button>
              <button @click="clearAuth" class="dev-btn danger">
                🗑️ 清除认证
              </button>
              <button @click="copyToken" class="dev-btn" :disabled="!hasToken">
                📋 复制Token
              </button>
              <button @click="openDevPage" class="dev-btn">
                🌐 打开测试页
              </button>
            </div>
          </div>

          <!-- 自定义登录 -->
          <div class="dev-section">
            <h4>自定义登录</h4>
            <div class="custom-login">
              <input 
                v-model="customUser.umtId" 
                placeholder="用户ID（可选）"
                class="dev-input"
              />
              <input 
                v-model="customUser.email" 
                placeholder="邮箱"
                class="dev-input"
              />
              <input 
                v-model="customUser.name" 
                placeholder="用户名"
                class="dev-input"
              />
              <button @click="customLogin" class="dev-btn primary full-width">
                登录
              </button>
            </div>
          </div>

          <!-- API测试 -->
          <div class="dev-section">
            <h4>API测试</h4>
            <div class="dev-actions">
              <button @click="testCheck" class="dev-btn">
                测试 /check
              </button>
              <button @click="testGetPhotos" class="dev-btn">
                测试 /getNew
              </button>
            </div>
            <div v-if="testResult" class="test-result">
              <pre>{{ testResult }}</pre>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DevTools',
  data() {
    return {
      isDevMode: process.env.VUE_APP_DEV_MODE === 'true',
      baseURL: process.env.VUE_APP_BASE_API,
      showPanel: false,
      hasToken: false,
      umtId: '',
      customUser: {
        umtId: '',
        email: 'test@example.com',
        name: '测试用户'
      },
      testResult: null
    };
  },
  mounted() {
    this.updateAuthStatus();
    // 监听localStorage变化
    window.addEventListener('storage', this.updateAuthStatus);
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.updateAuthStatus);
  },
  methods: {
    togglePanel() {
      this.showPanel = !this.showPanel;
    },
    updateAuthStatus() {
      this.hasToken = !!localStorage.getItem('Token-Circus');
      this.umtId = localStorage.getItem('umtId') || '';
    },
    async autoLogin() {
      try {
        const response = await axios.get(`${this.baseURL}/api/dev/mock-login`);
        const data = response.data;
        
        if (data.success) {
          localStorage.setItem('Token-Circus', data.token);
          localStorage.setItem('umtId', data.umtId);
          this.updateAuthStatus();
          this.$message.success(`登录成功: ${data.name}`);
        } else {
          this.$message.error('登录失败');
        }
      } catch (error) {
        this.$message.error('登录失败: ' + error.message);
      }
    },
    async customLogin() {
      try {
        const response = await axios.post(`${this.baseURL}/api/dev/custom-login`, this.customUser);
        const data = response.data;
        
        if (data.success) {
          localStorage.setItem('Token-Circus', data.token);
          localStorage.setItem('umtId', data.umtId);
          this.updateAuthStatus();
          this.$message.success(`登录成功: ${data.name}`);
        } else {
          this.$message.error('登录失败');
        }
      } catch (error) {
        this.$message.error('登录失败: ' + error.message);
      }
    },
    clearAuth() {
      localStorage.removeItem('Token-Circus');
      localStorage.removeItem('umtId');
      this.updateAuthStatus();
      this.$message.success('认证信息已清除');
    },
    copyToken() {
      const token = localStorage.getItem('Token-Circus');
      if (token) {
        navigator.clipboard.writeText(token).then(() => {
          this.$message.success('Token已复制到剪贴板');
        }).catch(() => {
          this.$message.error('复制失败');
        });
      }
    },
    openDevPage() {
      window.open(`${this.baseURL.replace('/galaxycircus', '')}/dev-login.html`, '_blank');
    },
    async testCheck() {
      try {
        const response = await axios.get(`${this.baseURL}/api/users/check`, {
          headers: {
            'Token-Circus': localStorage.getItem('Token-Circus')
          }
        });
        this.testResult = JSON.stringify(response.data, null, 2);
        this.$message.success('测试成功');
      } catch (error) {
        this.testResult = JSON.stringify({ error: error.message }, null, 2);
        this.$message.error('测试失败');
      }
    },
    async testGetPhotos() {
      try {
        const response = await axios.get(`${this.baseURL}/api/pic/getNew`, {
          headers: {
            'Token-Circus': localStorage.getItem('Token-Circus')
          }
        });
        this.testResult = JSON.stringify(response.data, null, 2);
        this.$message.success('测试成功');
      } catch (error) {
        this.testResult = JSON.stringify({ error: error.message }, null, 2);
        this.$message.error('测试失败');
      }
    }
  }
};
</script>

<style scoped>
.dev-tools {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
}

.dev-tools-toggle {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s;
  font-size: 24px;
}

.dev-tools-toggle:hover {
  transform: scale(1.1);
}

.dev-tools-panel {
  position: absolute;
  top: 60px;
  right: 0;
  width: 350px;
  max-height: 80vh;
  overflow-y: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.dev-tools-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px 20px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dev-tools-header h3 {
  margin: 0;
  font-size: 18px;
}

.dev-badge {
  background: rgba(255, 255, 255, 0.3);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.dev-tools-content {
  padding: 20px;
}

.dev-section {
  margin-bottom: 20px;
}

.dev-section h4 {
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #333;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 5px;
}

.dev-info {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 13px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.label {
  color: #666;
  font-weight: 500;
}

.value {
  color: #333;
  font-family: monospace;
}

.value.has-token {
  color: #28a745;
  font-weight: bold;
}

.dev-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.dev-btn {
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s;
  background: #f8f9fa;
  color: #333;
}

.dev-btn:hover:not(:disabled) {
  background: #e9ecef;
  transform: translateY(-1px);
}

.dev-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dev-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.dev-btn.primary:hover {
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.dev-btn.danger {
  background: #dc3545;
  color: white;
}

.dev-btn.danger:hover {
  background: #c82333;
}

.dev-btn.full-width {
  grid-column: 1 / -1;
}

.custom-login {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dev-input {
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 13px;
  transition: border-color 0.3s;
}

.dev-input:focus {
  outline: none;
  border-color: #667eea;
}

.test-result {
  margin-top: 10px;
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.test-result pre {
  margin: 0;
  font-size: 11px;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* 滚动条样式 */
.dev-tools-panel::-webkit-scrollbar {
  width: 6px;
}

.dev-tools-panel::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dev-tools-panel::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.dev-tools-panel::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 动画 */
.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter, .slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
